import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    users: 0,
    orders: 0,
    pendingOrders: 0,
    products: 0,
    banners: 0,
    addresses: 0,
    payments: "💰",
    walletTxns: "📜",
    referal: "👤",
    delivery: 0,
    withdraw: "💸",
    settings: "⚙️",
    awards: 0,
    achivements: 0,
    tree: 0,
    categories: 0,
    subCategories: 0,
    contacts: 0,
    todayReg: 0,
    todayUpgrades: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const role = (user?.role || "").toUpperCase();

    if (!token || !(role.includes("ADMIN") || role.includes("STAFF"))) {
      navigate("/login", { replace: true });
      return;
    }

    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const logout = () => {
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  const fetchCounts = async () => {
    try {
      const usersReq = axiosInstance
        .get("/api/users", { params: { role: "USER" } })
        .then((r) => r.data?.total ?? (r.data?.users?.length || 0))
        .catch(() => 0);

      const walletTxnsReq = Promise.resolve("📜");

      const productsReq = axiosInstance
        .get("/api/products")
        .then((r) => r.data?.total ?? (r.data?.products?.length || (Array.isArray(r.data) ? r.data.length : 0)))
        .catch(() => 0);

      const ordersReq = axiosInstance
        .get("/api/orders/admin")
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : (r.data?.orders || []);
          const total = Array.isArray(r.data) ? r.data.length : (r.data?.total || list.length || 0);
          const pending = list.filter(o => String(o.status || "").toUpperCase() === "PENDING").length;
          return { total, pending };
        })
        .catch(() => ({ total: 0, pending: 0 }));

      const bannersReq = axiosInstance
        .get("/api/banners")
        .then((r) => r.data?.total ?? (r.data?.banners?.length || 0))
        .catch(() => 0);

      const addressesReq = axiosInstance
        .get("/api/addresses/admin/all")
        .then((r) => r.data?.total ?? (r.data?.addresses?.length || 0))
        .catch(() => 0);

      const paymentsReq = Promise.resolve("💰"); // User requested icon instead of count



      const referalReq = Promise.resolve("👤");

      const deliveryReq = axiosInstance
        .get("/api/deliverycharges")
        .then((r) => r.data?.total ?? (r.data?.deliveryCharges?.length || (Array.isArray(r.data) ? r.data.length : 0)))
        .catch(() => 0);

      const withdrawReq = Promise.resolve("💸");

      const awardsReq = Promise.resolve("🏆");
      const achivementReq = Promise.resolve("🛡️");

      const treeReq = Promise.resolve("🌳");

      // ✅ FIX: categories endpoint
      const categoryReq = axiosInstance
        .get("/api/categories")
        .then((r) => r.data?.total ?? (r.data?.categories?.length || (Array.isArray(r.data) ? r.data.length : 0)))
        .catch(() => 0);

      // ✅ FIX: subcategories endpoint
      const subCategoryReq = axiosInstance
        .get("/api/subcategories")
        .then((r) => r.data?.total ?? (r.data?.subCategories?.length || (Array.isArray(r.data) ? r.data.length : 0)))
        .catch(() => 0);

      const contactReq = axiosInstance
        .get("/api/contacts/admin/unread-count")
        .then((r) => r.data?.unreadCount ?? r.data?.count ?? 0)
        .catch(() => 0);

      const billingReq = Promise.resolve("📂");

      const todayRegReq = axiosInstance
        .get("/api/reports/admin/registration-stats", { params: { period: "today" } })
        .then((r) => ({
          reg: r.data?.summary?.totalRegistrations ?? 0,
          up: r.data?.summary?.totalEntrepreneurUpgrades ?? 0,
        }))
        .catch(() => ({ reg: 0, up: 0 }));

      const [
        users,
        products,
        orders,
        banners,
        addresses,
        payments,
        walletTxns,
        referal,
        delivery,
        withdraw,
        awards,
        achivements,
        tree,
        categories,
        subCategories,
        billing,
        contacts,
        todayRegData,
      ] = await Promise.all([
        usersReq,
        productsReq,
        ordersReq,
        bannersReq,
        addressesReq,
        paymentsReq,
        walletTxnsReq,
        referalReq,
        deliveryReq,
        withdrawReq,
        awardsReq,
        achivementReq,
        treeReq,
        categoryReq,
        subCategoryReq,
        billingReq,
        contactReq,
        todayRegReq,
      ]);

      setCounts((prev) => ({
        ...prev,
        users,
        products,
        orders: orders.total,
        pendingOrders: orders.pending,
        banners,
        addresses,
        payments,
        walletTxns,
        referal,
        delivery,
        withdraw,
        awards,
        achivements,
        tree,
        categories,
        subCategories,
        billing,
        contacts,
        todayReg: todayRegData.reg,
        todayUpgrades: todayRegData.up,
      }));
    } catch (e) {
      console.log("fetchCounts error", e);
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0.2; }
          100% { opacity: 1; }
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <h2 style={{ margin: 0 }}>Admin Dashboard</h2>
          <div style={styles.sub}>Manage users, orders, products & admin settings</div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={logout} style={styles.logout}>
            Logout
          </button>
        </div>
      </div>

      <div style={styles.stats}>
        <StatCard title="Users" value={counts.users} onClick={() => navigate("/admin/users")} />
        <StatCard title="Orders" value={counts.orders} onClick={() => navigate("/admin/orders")} badge={counts.pendingOrders > 0 ? `New ${counts.pendingOrders}` : null} />
        <StatCard title="Categories" value={counts.categories} onClick={() => navigate("/admin/categories")} />
        <StatCard title="SubCategories" value={counts.subCategories} onClick={() => navigate("/admin/subcategories")} />

        <StatCard title="Products" value={counts.products} onClick={() => navigate("/admin/products")} />
        <StatCard title="Ads / Banners" value={counts.banners} onClick={() => navigate("/admin/banners")} />
        <StatCard title="Payments" value={counts.payments} onClick={() => navigate("/admin/payments")} />
        <StatCard title="Wallet Txns" value={counts.walletTxns} onClick={() => navigate("/admin/wallet-transactions")} />
        <StatCard title="Settings" value={counts.referal} onClick={() => navigate("/admin/referal")} />
        <StatCard title="Delivery Charges" value={counts.delivery} onClick={() => navigate("/admin/delivery")} />
        <StatCard title="Withdrawals" value={counts.withdraw} onClick={() => navigate("/admin/withdraw")} />
        <StatCard title="Awards" value={counts.awards} onClick={() => navigate("/admin/awards")} />
        <StatCard title="Achivements" value={counts.achivements} onClick={() => navigate("/admin/achivement")} />
        <StatCard title="Tree" value={counts.tree} onClick={() => navigate("/admin/tree")} />
        <StatCard title="Contact Inquiries" value={counts.contacts} onClick={() => navigate("/admin/contacts")} badge={counts.contacts > 0 ? `New ${counts.contacts}` : null} />

        {/* ✅ FIXED ROUTES */}
        <StatCard title="Today's Stats" value={`${counts.todayReg} / ${counts.todayUpgrades}`} onClick={() => navigate("/admin/registration-stats")} badge="TODAY" />
        <StatCard title="Reports (Charts)" value="📈" onClick={() => navigate("/admin/reports")} />
        <StatCard title="Stock Summary" value="📦" onClick={() => navigate("/admin/stock")} />
        <StatCard title="Billing" value={counts.billing} onClick={() => navigate("/admin/billing")} />
      </div>

      <div style={styles.hintBox}>
        💡 <b>Tip:</b> Click on a card to open the respective page.
      </div>
    </div>
  );
}

function StatCard({ title, value, onClick, badge }) {
  return (
    <div
      onClick={onClick}
      style={styles.statCard}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <div style={styles.cardTop}>
        <div style={styles.cardTitle}>{title}</div>
        {badge ? <span style={styles.badge}>{badge}</span> : null}
      </div>

      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardHint}>Click to open →</div>
    </div>
  );
}

const styles = {
  page: { padding: 20, background: "#f4f6f8", minHeight: "100vh" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  sub: { marginTop: 4, color: "#666", fontSize: 13 },
  logout: {
    padding: "10px 14px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #111",
    background: "#111",
    color: "#fff",
    fontWeight: 800,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
    marginTop: 18,
  },
  statCard: {
    background: "#fff",
    padding: 16,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    cursor: "pointer",
    userSelect: "none",
    border: "1px solid rgba(0,0,0,0.06)",
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
  cardTitle: { fontSize: 13, color: "#666", fontWeight: 700 },
  badge: {
    fontSize: 11,
    padding: "3px 8px",
    borderRadius: 999,
    background: "#ff0000",
    color: "#fff",
    fontWeight: 900,
    animation: "blink 1s infinite",
  },
  cardValue: { fontSize: 28, fontWeight: 900, marginTop: 8, color: "#111" },
  cardHint: { marginTop: 6, fontSize: 12, color: "#888" },
  hintBox: {
    marginTop: 16,
    background: "#fff",
    padding: 14,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  },
};
